import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, Trash2, Clock, User, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { 
  getActivities, 
  clearActivities, 
  exportActivitiesAsJSON,
  exportActivitiesAsCSV,
  getActivityStats,
  Activity,
  ActivityActions,
  Modules
} from '@/lib/activity-logger';

const ActivityLogs = () => {
  const [activities, setActivities] = useState<Activity[]>(getActivities());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterUser, setFilterUser] = useState('');

  const stats = useMemo(() => getActivityStats(), [activities]);

  // Get unique values for filters
  const uniqueActions = useMemo(() => Array.from(new Set(activities.map(a => a.action))), [activities]);
  const uniqueModules = useMemo(() => Array.from(new Set(activities.map(a => a.module))), [activities]);
  const uniqueUsers = useMemo(() => Array.from(new Set(activities.map(a => a.userName))), [activities]);

  // Filter activities
  const filteredActivities = useMemo(() => {
    return activities.filter(a => {
      const matchSearch = a.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          a.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          a.module.toLowerCase().includes(searchTerm.toLowerCase());
      const matchAction = !filterAction || a.action === filterAction;
      const matchModule = !filterModule || a.module === filterModule;
      const matchUser = !filterUser || a.userName === filterUser;
      
      return matchSearch && matchAction && matchModule && matchUser;
    });
  }, [activities, searchTerm, filterAction, filterModule, filterUser]);

  const handleExportJSON = () => {
    const json = exportActivitiesAsJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    toast.success('Activity logs exported as JSON');
  };

  const handleExportCSV = () => {
    const csv = exportActivitiesAsCSV();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Activity logs exported as CSV');
  };

  const handleClearLogs = () => {
    if (window.confirm('Adakah anda pasti ingin padam SEMUA activity logs? Tindakan ini tidak boleh di-undo!')) {
      clearActivities();
      setActivities([]);
      toast.success('All activity logs cleared');
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case ActivityActions.LOGIN: return 'bg-green-100 text-green-700';
      case ActivityActions.LOGOUT: return 'bg-blue-100 text-blue-700';
      case ActivityActions.CREATE: return 'bg-purple-100 text-purple-700';
      case ActivityActions.UPDATE: return 'bg-yellow-100 text-yellow-700';
      case ActivityActions.DELETE: return 'bg-red-100 text-red-700';
      case ActivityActions.EXPORT: return 'bg-cyan-100 text-cyan-700';
      case ActivityActions.PRINT: return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Activity Logs</h1>
          <p className="text-slate-600 mt-1">Audit trail of all system activities</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button 
            onClick={handleExportJSON}
            className="bg-green-600 hover:bg-green-700 text-white gap-2"
          >
            <Download size={18} />
            <span className="font-semibold">JSON</span>
          </Button>
          <Button 
            onClick={handleExportCSV}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          >
            <Download size={18} />
            <span className="font-semibold">CSV</span>
          </Button>
          <Button 
            onClick={handleClearLogs}
            variant="destructive"
            className="gap-2"
          >
            <Trash2 size={18} />
            <span className="font-semibold">Clear All</span>
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Total Activities</p>
              <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500">Semua aktivitas dicatat</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Unique Users</p>
              <p className="text-3xl font-bold text-blue-600">{uniqueUsers.length}</p>
              <p className="text-xs text-slate-500">Pengguna aktif</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Action Types</p>
              <p className="text-3xl font-bold text-purple-600">{uniqueActions.length}</p>
              <p className="text-xs text-slate-500">Jenis aktivitas</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Modules</p>
              <p className="text-3xl font-bold text-orange-600">{uniqueModules.length}</p>
              <p className="text-xs text-slate-500">Modul yang diakses</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Cari</label>
              <Input
                placeholder="Cari user, module, atau details..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-slate-300"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Action</label>
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">Semua Action</option>
                {uniqueActions.map(action => (
                  <option key={action} value={action}>{action}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Module</label>
              <select
                value={filterModule}
                onChange={(e) => setFilterModule(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">Semua Module</option>
                {uniqueModules.map(module => (
                  <option key={module} value={module}>{module}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">User</label>
              <select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">Semua User</option>
                {uniqueUsers.map(user => (
                  <option key={user} value={user}>{user}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Table */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
          <CardDescription>Total {filteredActivities.length} activities</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredActivities.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock size={48} className="mx-auto mb-4 opacity-50" />
              <p>No activities found matching your filters</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredActivities.slice().reverse().map((activity) => (
                <div key={activity.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getActionColor(activity.action)}`}>
                          {activity.action}
                        </span>
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                          {activity.module}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 mb-2">{activity.details}</p>
                      <div className="flex gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {activity.userName} ({activity.userId})
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {new Date(activity.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Summary by Module */}
      {Object.keys(stats.byModule).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Activity Summary by Module</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(stats.byModule).sort((a, b) => b[1] - a[1]).map(([module, count]) => (
                <div key={module} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-slate-600">{module}</p>
                      <p className="text-2xl font-bold text-slate-900">{count}</p>
                    </div>
                    <FileText size={32} className="text-slate-300" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity Summary by User */}
      {Object.keys(stats.byUser).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Activity Summary by User</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.byUser).sort((a, b) => b[1] - a[1]).map(([user, count]) => (
                <div key={user} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-900">{user}</span>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-700">
                    {count} activities
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ActivityLogs;
